import React, { useState } from 'react';
import { 
  Wallet,
  Plus,
  Copy,
  Eye,
  EyeOff,
  Check,
  X,
  Edit2,
  Trash2,
  ExternalLink
} from 'lucide-react';

const CompanyCryptoWallet = () => {
  const [wallets, setWallets] = useState([
    { 
      id: 1, 
      address: '0xC0AdA23f7C5efECFE70fb9D261a3CC5B1162eB0D', 
      type: 'USDT(TRC-20)', 
      status: 'active',
      network: 'TRC20'
    },
    { 
      id: 2, 
      address: '0x007CE47de14Fd61Cf3d33cdd5f1f00AaDdE1A3ab', 
      type: 'USDT(ERC-20)', 
      status: 'inactive',
      network: 'ERC20'
    },
    { 
      id: 3, 
      address: '0xB36f54e361B310ED0597149CCd905cD73f14E954', 
      type: 'BTC', 
      status: 'inactive',
      network: 'Bitcoin'
    },
    { 
      id: 4, 
      address: '0x759638aeAA8E2efBb1D13Bc360fd68A9a5814B58', 
      type: 'ETH', 
      status: 'inactive',
      network: 'Ethereum'
    },
    { 
      id: 5, 
      address: '0x4846635526EF935FE282D3c4E7609408776b5F50', 
      type: 'BNB', 
      status: 'inactive',
      network: 'BSC'
    }
  ]);

  const [newWallet, setNewWallet] = useState({
    type: 'USDT(TRC-20)',
    address: '',
    network: 'TRC20'
  });

  const [showAddress, setShowAddress] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [editingWallet, setEditingWallet] = useState(null);
  const [editForm, setEditForm] = useState({ address: '', type: '' });

  const cryptoTypes = [
    'USDT(TRC-20)',
    'USDT(ERC-20)',
    'BTC',
    'ETH',
    'BNB',
    'SOL',
    'XRP',
    'ADA',
    'DOGE'
  ];

  const handleAddWallet = (e) => {
    e.preventDefault();
    if (!newWallet.address.trim()) {
      alert('Please enter wallet address');
      return;
    }

    const newWalletObj = {
      id: wallets.length + 1,
      address: newWallet.address,
      type: newWallet.type,
      status: 'inactive',
      network: newWallet.type.includes('TRC') ? 'TRC20' : 
               newWallet.type.includes('ERC') ? 'ERC20' :
               newWallet.type === 'BTC' ? 'Bitcoin' :
               newWallet.type === 'ETH' ? 'Ethereum' :
               newWallet.type === 'BNB' ? 'BSC' : 'Other'
    };

    setWallets([...wallets, newWalletObj]);
    setNewWallet({ type: 'USDT(TRC-20)', address: '', network: 'TRC20' });
    alert('Wallet added successfully!');
  };

  const toggleWalletStatus = (id) => {
    setWallets(wallets.map(wallet => {
      if (wallet.id === id) {
        return { ...wallet, status: wallet.status === 'active' ? 'inactive' : 'active' };
      }
      return { ...wallet, status: 'inactive' }; // Only one wallet can be active at a time
    }));
  };

  const copyToClipboard = (address) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const startEdit = (wallet) => {
    setEditingWallet(wallet.id);
    setEditForm({ address: wallet.address, type: wallet.type });
  };

  const saveEdit = () => {
    if (!editForm.address.trim()) {
      alert('Address cannot be empty');
      return;
    }

    setWallets(wallets.map(wallet => 
      wallet.id === editingWallet 
        ? { ...wallet, address: editForm.address, type: editForm.type }
        : wallet
    ));
    setEditingWallet(null);
    alert('Wallet updated successfully!');
  };

  const cancelEdit = () => {
    setEditingWallet(null);
    setEditForm({ address: '', type: '' });
  };

  const deleteWallet = (id) => {
    if (window.confirm('Are you sure you want to delete this wallet?')) {
      setWallets(wallets.filter(wallet => wallet.id !== id));
      alert('Wallet deleted successfully!');
    }
  };

  const truncateAddress = (address) => {
    if (showAddress) return address;
    return `${address.substring(0, 8)}...${address.substring(address.length - 6)}`;
  };

  const getNetworkColor = (network) => {
    switch(network) {
      case 'TRC20': return 'text-yellow-400 bg-yellow-500/20';
      case 'ERC20': return 'text-blue-400 bg-blue-500/20';
      case 'Bitcoin': return 'text-orange-400 bg-orange-500/20';
      case 'Ethereum': return 'text-purple-400 bg-purple-500/20';
      case 'BSC': return 'text-yellow-600 bg-yellow-600/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  return (
    <div className="max-w-7xl mx-auto pt-10 px-4">
      {/* Header */}
      <div className="mb-8">
       
        <h1 className="text-3xl font-bold text-gold-100">Company Crypto Wallet</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Add New Wallet */}
        <div>
          <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-8 mb-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 rounded-full bg-gold-500/20 flex items-center justify-center mr-4">
                <Plus className="h-6 w-6 text-gold-400" />
              </div>
              <h2 className="text-xl font-semibold text-gold-100">Add New Wallet</h2>
            </div>

            <form onSubmit={handleAddWallet} className="space-y-6">
              {/* Account Type */}
              <div>
                <label className="block text-gold-300 mb-2">Account Type</label>
                <select
                  value={newWallet.type}
                  onChange={(e) => setNewWallet({...newWallet, type: e.target.value})}
                  className="w-full px-4 py-3 bg-gold-900/50 border border-gold-600/50 rounded-xl text-gold-100 focus:outline-none focus:border-gold-500"
                >
                  {cryptoTypes.map(type => (
                    <option key={type} value={type} className="bg-gold-900">{type}</option>
                  ))}
                </select>
              </div>

              {/* Account Address */}
              <div>
                <label className="block text-gold-300 mb-2">Account Address</label>
                <div className="relative">
                  <input
                    type="text"
                    value={newWallet.address}
                    onChange={(e) => setNewWallet({...newWallet, address: e.target.value})}
                    placeholder="Enter Account Address"
                    className="w-full px-4 py-3 bg-gold-900/50 border border-gold-600/50 rounded-xl text-gold-100 placeholder-gold-600 focus:outline-none focus:border-gold-500"
                  />
                </div>
              </div>

              {/* Network Info */}
              <div className="p-4 bg-gold-900/20 rounded-xl border border-gold-600/30">
                <div className="text-sm text-gold-400 mb-2">Selected Network:</div>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getNetworkColor(newWallet.network)}`}>
                  {newWallet.network}
                </div>
              </div>

              {/* Add Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-3 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl transition-all flex items-center justify-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Wallet Address
                </button>
              </div>
            </form>

            {/* Instructions */}
            <div className="mt-8 pt-6 border-t border-gold-800/30">
              <h3 className="font-medium text-gold-200 mb-3">Important Notes</h3>
              <ul className="text-sm text-gold-500 space-y-2 list-disc list-inside">
                <li>Only one wallet can be active at a time</li>
                <li>Ensure the address matches the selected network</li>
                <li>Double-check the address before saving</li>
                <li>Test with a small amount first</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Column - Wallet List */}
        <div>
          <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gold-100 flex items-center">
                <Wallet className="h-5 w-5 mr-2" />
                Wallet Addresses
              </h2>
              <button
                onClick={() => setShowAddress(!showAddress)}
                className="flex items-center px-4 py-2 bg-gold-500/20 hover:bg-gold-500/30 text-gold-300 rounded-lg transition-colors"
              >
                {showAddress ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide Addresses
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Addresses
                  </>
                )}
              </button>
            </div>

            {/* Wallets Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gold-900/50">
                  <tr>
                    <th className="py-3 px-4 text-left text-gold-300 font-semibold">Address</th>
                    <th className="py-3 px-4 text-left text-gold-300 font-semibold">Type</th>
                    <th className="py-3 px-4 text-left text-gold-300 font-semibold">Network</th>
                    <th className="py-3 px-4 text-left text-gold-300 font-semibold">Status</th>
                    <th className="py-3 px-4 text-left text-gold-300 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gold-800/50">
                  {wallets.map(wallet => (
                    <tr key={wallet.id} className="hover:bg-gold-900/20 transition-colors">
                      <td className="py-3 px-4">
                        {editingWallet === wallet.id ? (
                          <input
                            type="text"
                            value={editForm.address}
                            onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                            className="w-full px-3 py-1 bg-gold-900/50 border border-gold-600/50 rounded text-gold-100"
                          />
                        ) : (
                          <div className="group">
                            <div className="font-mono text-sm text-gold-300 flex items-center">
                              <span>{truncateAddress(wallet.address)}</span>
                              <div className="ml-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => copyToClipboard(wallet.address)}
                                  className="p-1 hover:bg-gold-900/50 rounded transition-colors"
                                  title="Copy address"
                                >
                                  {copiedAddress === wallet.address ? (
                                    <Check className="h-3 w-3 text-green-400" />
                                  ) : (
                                    <Copy className="h-3 w-3 text-gold-400" />
                                  )}
                                </button>
                                <button
                                  onClick={() => window.open(`https://tronscan.org/#/address/${wallet.address}`, '_blank')}
                                  className="p-1 hover:bg-gold-900/50 rounded transition-colors"
                                  title="View on explorer"
                                >
                                  <ExternalLink className="h-3 w-3 text-gold-400" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingWallet === wallet.id ? (
                          <select
                            value={editForm.type}
                            onChange={(e) => setEditForm({...editForm, type: e.target.value})}
                            className="px-3 py-1 bg-gold-900/50 border border-gold-600/50 rounded text-gold-100 text-sm"
                          >
                            {cryptoTypes.map(type => (
                              <option key={type} value={type} className="bg-gold-900">{type}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-gold-300 text-sm">{wallet.type}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getNetworkColor(wallet.network)}`}>
                          {wallet.network}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {wallet.status === 'active' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                            <Check className="h-3 w-3 mr-1" />
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                            <X className="h-3 w-3 mr-1" />
                            Disabled
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingWallet === wallet.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={saveEdit}
                              className="px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded text-sm transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => toggleWalletStatus(wallet.id)}
                              className={`px-3 py-1 rounded text-sm transition-colors ${
                                wallet.status === 'active'
                                  ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                                  : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                              }`}
                            >
                              {wallet.status === 'active' ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => startEdit(wallet)}
                              className="p-1 hover:bg-gold-900/50 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4 text-gold-400" />
                            </button>
                            <button
                              onClick={() => deleteWallet(wallet.id)}
                              className="p-1 hover:bg-red-500/20 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Stats */}
            <div className="mt-8 pt-6 border-t border-gold-800/30">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gold-900/20 rounded-xl p-4">
                  <div className="text-gold-400 text-sm mb-1">Total Wallets</div>
                  <div className="text-2xl font-bold text-gold-100">{wallets.length}</div>
                </div>
                <div className="bg-gold-900/20 rounded-xl p-4">
                  <div className="text-gold-400 text-sm mb-1">Active Wallet</div>
                  <div className="text-2xl font-bold text-green-400">
                    {wallets.filter(w => w.status === 'active').length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyCryptoWallet;